import { useAuth } from "@workos-inc/authkit-react";
import { useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router";
import Sidebar from "./components/sidebar";
import { useAdminMode } from "./contexts/AdminModeContext";
import "./App.css";
import { ColorProvider } from "./contexts/ColorContext";
import { appConfig } from "@/appConfig";

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role, isLoading, signIn } = useAuth();
  const { adminMode } = useAdminMode();

  useEffect(() => {
    if (location.pathname.includes("login")) {
      const searchParams = new URLSearchParams(location.search);
      const context = searchParams.get("context") ?? undefined;
      signIn({ context });
    }
  }, [location.pathname, location.search, signIn]);

  useEffect(() => {
    if (!isLoading && user) {
      const userRole = role;
      const currentPath = location.pathname;

      if (userRole === "member" && !currentPath.startsWith("/assistant")) {
        navigate("/assistant", { replace: true });
      }

      if (userRole === "admin") {
        // If admin is in member mode, redirect to assistant
        if (adminMode === "member" && !currentPath.startsWith("/assistant")) {
          navigate("/assistant", { replace: true });
        }
        // If admin is in admin mode, redirect to admin pages
        else if (adminMode === "admin") {
          const allowedAdminRoutes = [
            "/documents",
            "/users",
            "/answers",
            "/settings",
          ];
          const pathSegments = currentPath.split("/").filter(Boolean);
          const isAllowedAdminRoute = allowedAdminRoutes.some((route) => {
            const normalizedRoute = route.replace(/^\//, "");
            if (
              currentPath === route ||
              currentPath.startsWith(`${route}/`)
            ) {
              return true;
            }
            return pathSegments.includes(normalizedRoute);
          });

          if (!isAllowedAdminRoute) {
            navigate("/documents", { replace: true });
          }
        }
      }
    }
  }, [user, role, isLoading, navigate, adminMode, location.pathname]);

  if (isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <div className="flex space-x-2 transform translate-y-[0.75px]">
          <div className="w-2 h-2 rounded-full animate-bounce [animation-delay:-0.3s]" style={{ backgroundColor: appConfig.themeColor }} />
          <div className="w-2 h-2 rounded-full animate-bounce [animation-delay:-0.15s]" style={{ backgroundColor: appConfig.themeColor }} />
          <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: appConfig.themeColor }} />
        </div>
      </div>
    );
  }

  if (!user) {
    navigate("/auth");
    return null;
  }

  return (
    <ColorProvider>
      <div
        className="flex h-screen w-screen"
        style={{
          background: `linear-gradient(0deg, #FFFFFF, #FFFFFF), linear-gradient(180deg, rgba(238, 242, 255, 0) 81.26%, #EEF2FF 99.1%)`,
        }}
      >
        <Sidebar />
        <main className="flex-1 overflow-hidden pt-16 lg:pt-0">
          <Outlet />
        </main>
      </div>
    </ColorProvider>
  );
};

export default App;
